/**
 * useIsMobile Hook
 *
 * Detects if the current viewport is mobile-sized (< 768px).
 * Uses window.matchMedia for efficient, event-driven updates.
 *
 * @example
 * ```tsx
 * const isMobile = useIsMobile();
 *
 * return (
 *   <div>
 *     {isMobile ? <MobileNav /> : <DesktopNav />}
 *   </div>
 * );
 * ```
 */

import { useState, useEffect } from 'react';

/**
 * Mobile breakpoint threshold (matches Tailwind's md breakpoint)
 */
const MOBILE_BREAKPOINT = 768;

/**
 * Hook to detect mobile viewport
 *
 * @returns boolean - true if viewport width < 768px
 */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    // Initialize with current state (SSR-safe)
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  useEffect(() => {
    // Use matchMedia for efficient event-driven updates
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

    // Handler for media query changes
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(event.matches);
    };

    // Set initial state
    handleChange(mediaQuery);

    // Add listener (use deprecated API for older browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange as any);
    }

    // Cleanup listener
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange as any);
      }
    };
  }, []);

  return isMobile;
};
